<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>Chapel Schedule</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-menu v-model="termMenu">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" text>
            {{ selectedTerm.title }}
            <v-icon right>fal fa-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item v-for="(item, index) in termOptions" :key="'term-' + index" @click="selectedTerm = item">
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.remaining === 0">Required credits met</v-list-item-subtitle>
              <v-list-item-subtitle v-else>{{ item.remaining }} out of {{ item.required }} credits needed</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col>
          <v-text-field :value="status.required" label="Required Credits" outlined readonly />
        </v-col>
        <v-col>
          <v-text-field :value="status.received" label="Received Credits" outlined readonly />
        </v-col>
        <v-col>
          <v-text-field :value="status.remaining" label="Remaining Credits" outlined readonly />
        </v-col>
        <v-col>
          <v-text-field :value="missed" label="Missed Chapels" outlined readonly />
        </v-col>
      </v-row>
    </v-card-text>
    <v-data-table :items="chapels" :headers="headers" :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }">
      <template v-slot:item.date="{ item }">{{ stringFormatDate(item.date) }}</template>
      <template v-slot:item.credit="{ item }">
        <chapel-summary v-if="item.hasCredit === 'N' && item.summaryAllowed === 'Y'" :pidm="user.pidm" :chapel="item" :term="selectedTerm" :tooltip="item.type === 'Summary' ? 'Summary Rejected. Reason: ' + item.reason : ''" @submitted="load()"></chapel-summary>
        <v-tooltip top v-else-if="item.hasCredit === 'N'">
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" text>{{ item.hasCredit }}</v-btn>
          </template>
          <div>Summaries are not allowed for this chapel</div>
        </v-tooltip>
        <v-tooltip top v-else>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" text>{{ item.hasCredit }}</v-btn>
          </template>
          <div>Credit Type: {{ item.type }}</div>
          <div v-if="item.reason">Description: {{ item.reason }}</div>
          <div v-if="item.creditValue">Credits: {{ item.creditValue }}</div>
        </v-tooltip>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import { stringFormatDate } from '../../helpers/formatters'
import { ref, computed, watch, onMounted } from '@vue/composition-api'

export default {
  components: {
    ChapelSummary: () => import('@/components/chapel/summary')
  },
  setup (props, { root }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const service = root.$feathers.service('chapel/student')

    const termMenu = ref(false)
    const selectedTerm = ref('')
    const termOptions = ref([])
    const status = ref({
      required: 0,
      received: 0,
      remaining: 0
    })

    async function load () {
      const { _id, term } = selectedTerm.value
      const aggregate = [
        { $match: { term } },
        { $lookup: {
          as: 'credit',
          from: 'Chapel-Students',
          let: { 'chapelId': '$_id' },
          pipeline: [
            { $match: { term, _id } },
            { $unwind: '$credits' },
            { $project: { chapelId: '$credits.chapel', date: '$credits.date', type: '$credits.type', credit: '$credits.credit', reason: '$credits.reason' } },
            { $match: { $expr: { $eq: ['$chapelId', '$$chapelId'] } } }
          ]
        } },
        { $addFields: { credit: { $first: '$credit' } } },
        { $project: {
          _id: 1,
          date: 1,
          isMakeup: 1,
          speaker: 1,
          title: 1,
          hasCredit: { $switch: {
            branches: [
              { case: { $gte: ['$credit.credit', 1] }, then: 'Y' },
              {
                case: { $and: [
                  { $eq: ['$credit.type', 'Summary'] },
                  { $eq: ['$credit.reason', 'Summary Submitted; Pending Review'] }
                ] },
                then: 'P'
              }
            ],
            default: 'N'
          } },
          creditValue: '$credit.credit',
          creditType: { $ifNull: ['$credit.type', 'No Credit Entry'] },
          reason: '$credit.reason',
          summaryAllowed: { $switch: {
            branches: [
              { case: { $gte: ['$credits.Summary', 1] }, then: 'Y' }
            ],
            default: 'N'
          } }
        } },
        { $match: {
          $or: [{ isMakeup: { $exists: false } }, { isMakeup: false }, { hasCredit: 'Y' }]
        } },
        { $sort: { date: 1 } }
      ]
      const { data } = await root.$feathers.service('chapel/schedule').find({ query: { aggregate } })
      chapels.value = data
    }

    watch(selectedTerm, ({ _id, term }) => {
      if (_id != null) {
        load()
        const rec = termOptions.value.find(({ term: t }) => t === term)
        if (rec) {
          status.value = {
            required: rec.required,
            received: rec.received,
            remaining: rec.remaining
          }
        } else {
          status.value = {
            required: 0,
            received: 0,
            remaining: 0
          }
        }
      }
    })

    const chapels = ref([])
    const missed = ref(0)

    async function loadMissed () {
      const { data: stuData } = await root.$feathers.service('chapel/student').find({ query: { pidm: user.value.pidm, $sort: { term: -1 } } })
      const term = stuData[0].term
      const chapels = stuData[0].credits.map(({ chapel }) => chapel)
      const aggregate = [
        { $addFields: { MWF: { $mod: [{ $dayOfWeek: '$date' }, 2] }, time: { $hour: { date: '$date', timezone: 'America/New_York' } } } },
        { $match: { term, $or: [{ isMakeup: false }, { isMakeup: { $exists: false } }], $expr: { $lte: ['$date', '$$NOW'] }, MWF: 0, time: 11, _id: { $nin: chapels } } }
      ]
      const { data } = await root.$feathers.service('chapel/schedule').find({ query: { aggregate } })
      missed.value = data.length
    }

    onMounted(async () => {
      const aggregate = [
        { $match: { pidm: user.value.pidm } },
        { $addFields: { received: { $sum: '$credits.credit' } } },
        { $project: {
          _id: 1,
          term: 1,
          required: 1,
          received: 1,
          remaining: { $max: [0, { $subtract: ['$required', '$received'] }] }
        } },
        { $sort: { term: -1 } }
      ]
      const { data: aggData } = await service.find({ query: { aggregate } })
      aggData.forEach(({ _id, term, required, received, remaining }) => {
        let year = term.substr(0, 4)
        let suffix = term.substr(-2)
        let desc = ''
        switch (suffix) {
          case '05':
            desc = 'Spring'
            break
          case '60':
            desc = 'Fall'
            break
          case '02':
            desc = 'Jan Term'
            break
          case '10':
            desc = 'May Term'
            break
        }
        let title = desc + ' ' + year
        termOptions.value.push({ _id, term, title, required, received, remaining })
        if (selectedTerm.value === '') selectedTerm.value = termOptions.value[0]
      })
      loadMissed()
    })

    const headers = ref([
      { text: 'Date', value: 'date' },
      { text: 'Speaker', value: 'speaker' },
      { text: 'Title', value: 'title' },
      { text: 'Credit', value: 'credit' }
    ])

    return {
      user,
      termMenu,
      selectedTerm,
      termOptions,
      status,
      chapels,
      missed,
      headers,
      load,
      stringFormatDate
    }
  }
}
</script>
